import { localApiBase } from "../../lib/localApi";
// Typed REST client for the extension server's chat-bot API.
//
// Endpoints (all served by the local extension server — same host/port as
// the integrations API, resolved by `lib/localApi`, default 55500):
//
//   GET/PUT             /api/bot/settings
//   GET/POST/PUT/DELETE /api/bot/commands
//   GET/POST/PUT/DELETE /api/bot/timers
//   GET/PUT             /api/bot/filters
//   GET                 /api/bot/status
//   POST                /api/bot/permit
//   POST                /api/integrations/chat/send
//   POST                /api/integrations/chat/moderate
//
// This file is the single source of truth for the bot wire types on the
// renderer side — other agents/components import the types from here.
// Keep the exported surface stable.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BotPlatform = "twitch" | "youtube" | "kick";

export const BOT_PLATFORMS: readonly BotPlatform[] = [
  "twitch",
  "youtube",
  "kick",
] as const;

export type BotPermission =
  | "everyone"
  | "subscriber"
  | "vip"
  | "moderator"
  | "broadcaster";

export type BotFilterAction = "delete" | "timeout" | "ban";

export type BotModerationAction = "delete" | "timeout" | "ban" | "unban";

export interface BotSettings {
  enabled: boolean;
  prefix: string;
  platforms: Record<BotPlatform, boolean>;
  youtubeDailyBudgetUnits: number;
  youtubeMentionOnly: boolean;
  exemptRoles: string[];
}

export interface BotCommand {
  id: string;
  name: string;
  response: string;
  permission: BotPermission;
  cooldownGlobalSec: number;
  cooldownUserSec: number;
  enabled: boolean;
  platforms?: BotPlatform[];
}

/** Payload to create a command — the server assigns the `id`. */
export type BotCommandInput = Omit<BotCommand, "id">;

export interface BotTimer {
  id: string;
  name: string;
  text: string;
  intervalMinutes: number;
  minChatLines: number;
  enabled: boolean;
}

/** Payload to create a timer — the server assigns the `id`. */
export type BotTimerInput = Omit<BotTimer, "id">;

export interface BotBannedWordsFilter {
  enabled: boolean;
  words: string[];
  regexes: string[];
  action: BotFilterAction;
}

export interface BotLinksFilter {
  enabled: boolean;
  action: BotFilterAction;
}

export interface BotCapsFilter {
  enabled: boolean;
  minLength: number;
  maxPercent: number;
  action: BotFilterAction;
}

export interface BotEmotesFilter {
  enabled: boolean;
  max: number;
  action: BotFilterAction;
}

export interface BotRepetitionFilter {
  enabled: boolean;
  action: BotFilterAction;
}

export interface BotEscalation {
  timeoutSeconds: number[];
  banAfter: number;
}

export interface BotFilters {
  bannedWords: BotBannedWordsFilter;
  links: BotLinksFilter;
  caps: BotCapsFilter;
  emotes: BotEmotesFilter;
  repetition: BotRepetitionFilter;
  escalation: BotEscalation;
  exemptRoles: string[];
}

export interface BotPlatformStatus {
  connected: boolean;
  canWrite: boolean;
  needsReauth: boolean;
}

export interface BotStatus {
  running: boolean;
  platforms: Partial<Record<BotPlatform, BotPlatformStatus>>;
  youtubeUnitsUsedToday: number;
}

export interface ChatSendRequest {
  platform?: BotPlatform | string;
  text: string;
}

export interface ChatModerateRequest {
  platform: BotPlatform | string;
  action: BotModerationAction;
  messageId?: string;
  userId?: string;
  userName?: string;
  durationSeconds?: number;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

/**
 * Some extension-server endpoints wrap payloads in `{ data: ... }`,
 * others return the resource directly. Unwrap defensively so the client
 * works with either envelope.
 */
const unwrap = <T>(json: unknown): T => {
  if (
    json !== null &&
    typeof json === "object" &&
    "data" in (json as Record<string, unknown>) &&
    (json as Record<string, unknown>).data !== undefined
  ) {
    return (json as { data: T }).data;
  }
  return json as T;
};

class BotService {
  private domain(): string {
    return localApiBase();
  }

  private async request<T>(
    path: string,
    init?: RequestInit & { body?: BodyInit | null },
  ): Promise<T> {
    const response = await fetch(`${this.domain()}${path}`, {
      headers: { "Content-Type": "application/json" },
      ...init,
    });
    if (!response.ok) {
      let detail = "";
      try {
        detail = await response.text();
      } catch {
        // ignore
      }
      throw new Error(
        `Bot API ${init?.method ?? "GET"} ${path} failed (${response.status})${
          detail ? `: ${detail.slice(0, 200)}` : ""
        }`,
      );
    }
    // DELETE / some PUTs may answer 204 with an empty body.
    const text = await response.text();
    if (!text) return undefined as T;
    return unwrap<T>(JSON.parse(text));
  }

  /**
   * Item-level PUT/DELETE: prefer the conventional `/:id` route, fall
   * back to the bare collection path (id in body) if the server mounted
   * it that way — the contract only pins the collection path.
   */
  private async requestItem<T>(
    collectionPath: string,
    id: string,
    method: "PUT" | "DELETE",
    body?: unknown,
  ): Promise<T> {
    const payload =
      body === undefined
        ? JSON.stringify({ id })
        : JSON.stringify({ ...(body as object), id });
    try {
      return await this.request<T>(
        `${collectionPath}/${encodeURIComponent(id)}`,
        { method, body: payload },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (/\((404|405)\)/.test(message)) {
        return this.request<T>(collectionPath, { method, body: payload });
      }
      throw error;
    }
  }

  // -- Settings -------------------------------------------------------------

  getSettings(): Promise<BotSettings> {
    return this.request<BotSettings>("/api/bot/settings");
  }

  updateSettings(settings: BotSettings): Promise<BotSettings> {
    return this.request<BotSettings>("/api/bot/settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  }

  // -- Commands ---------------------------------------------------------------

  getCommands(): Promise<BotCommand[]> {
    return this.request<BotCommand[]>("/api/bot/commands");
  }

  createCommand(command: BotCommandInput): Promise<BotCommand> {
    return this.request<BotCommand>("/api/bot/commands", {
      method: "POST",
      body: JSON.stringify(command),
    });
  }

  updateCommand(command: BotCommand): Promise<BotCommand> {
    return this.requestItem<BotCommand>(
      "/api/bot/commands",
      command.id,
      "PUT",
      command,
    );
  }

  deleteCommand(id: string): Promise<void> {
    return this.requestItem<void>("/api/bot/commands", id, "DELETE");
  }

  // -- Timers -----------------------------------------------------------------

  getTimers(): Promise<BotTimer[]> {
    return this.request<BotTimer[]>("/api/bot/timers");
  }

  createTimer(timer: BotTimerInput): Promise<BotTimer> {
    return this.request<BotTimer>("/api/bot/timers", {
      method: "POST",
      body: JSON.stringify(timer),
    });
  }

  updateTimer(timer: BotTimer): Promise<BotTimer> {
    return this.requestItem<BotTimer>(
      "/api/bot/timers",
      timer.id,
      "PUT",
      timer,
    );
  }

  deleteTimer(id: string): Promise<void> {
    return this.requestItem<void>("/api/bot/timers", id, "DELETE");
  }

  // -- Filters ----------------------------------------------------------------

  getFilters(): Promise<BotFilters> {
    return this.request<BotFilters>("/api/bot/filters");
  }

  updateFilters(filters: BotFilters): Promise<BotFilters> {
    return this.request<BotFilters>("/api/bot/filters", {
      method: "PUT",
      body: JSON.stringify(filters),
    });
  }

  // -- Status -----------------------------------------------------------------

  getStatus(signal?: AbortSignal): Promise<BotStatus> {
    return this.request<BotStatus>("/api/bot/status", { signal });
  }

  // -- Chat actions -------------------------------------------------------------

  async sendChatMessage(payload: ChatSendRequest): Promise<void> {
    const result = await this.request<{
      results: Array<{ platform: string; ok: boolean; error?: string }>;
    }>("/api/integrations/chat/send", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    if (!result?.results?.length || result.results.some((item) => !item.ok)) {
      throw new Error("Chat message was not delivered to every selected platform");
    }
  }

  moderateChat(payload: ChatModerateRequest): Promise<void> {
    return this.request<void>("/api/integrations/chat/moderate", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  permitUser(userName: string): Promise<void> {
    return this.request<void>("/api/bot/permit", {
      method: "POST",
      body: JSON.stringify({ userName }),
    });
  }
}

export const botService = new BotService();
export default botService;
